import { defineStore } from 'pinia';
import api from '../utils/api';

export const useScanStore = defineStore('scan', {
  state: () => ({
    uploadQueue: [], // { id, file, status, progress, result, error, invoiceId, processingTime, processingProgress, processingTimer }
    scans: [],
    currentScan: null,
    maxConcurrent: 3,
    activeUploads: 0,
    pollingInterval: null,
    loading: false,
    processingTimers: {}, // Map of uploadId to interval
    uploadMetadata: {}, // Store company_id and type for uploads
  }),

  getters: {
    queuedFiles: (state) =>
      state.uploadQueue.filter(item => item.status === 'queued'),
    uploadingFiles: (state) =>
      state.uploadQueue.filter(item => item.status === 'uploading'),
    completedFiles: (state) =>
      state.uploadQueue.filter(item => item.status === 'completed'),
    failedFiles: (state) =>
      state.uploadQueue.filter(item => item.status === 'failed'),
    processingScans: (state) =>
      state.scans.filter(scan => scan.status === 'pending' || scan.status === 'processing'),
  },

  actions: {
    // Set upload metadata (company_id, type)
    setUploadMetadata(metadata) {
      this.uploadMetadata = metadata;
    },

    // Add files to upload queue
    addFiles(filesWithPreviews) {
      const fileArray = Array.isArray(filesWithPreviews) ? filesWithPreviews : Array.from(filesWithPreviews);
      // Reverse the array so when we unshift, the first file selected appears at the top
      fileArray.reverse().forEach(item => {
        const file = item.file || item;
        const preview = item.preview || null;
        const id = `upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        // Use unshift to add to the beginning of the queue
        this.uploadQueue.unshift({
          id,
          file,
          preview,
          fileName: file.name,
          fileSize: file.size,
          status: 'queued',
          progress: 0,
          result: null,
          error: null,
          invoiceId: null,
          processingTime: 0,
          processingProgress: 0,
          scanId: null,
          // Store metadata with the item
          company_id: this.uploadMetadata.company_id || null,
          type: this.uploadMetadata.type || null,
          is_hard_scan: this.uploadMetadata.is_hard_scan || false,
          // Pages of ONE invoice stitched into a single image: the server must NOT
          // split it back into several invoices.
          fromMultiPage: item.fromMultiPage || false,
        });
      });

      // Start uploading
      this.processQueue();
    },

    // Process upload queue
    async processQueue() {
      while (this.activeUploads < this.maxConcurrent && this.queuedFiles.length > 0) {
        const nextFile = this.queuedFiles[0];
        await this.uploadFile(nextFile.id);
      }
    },

    // Upload a single file
    async uploadFile(uploadId) {
      const fileItem = this.uploadQueue.find(item => item.id === uploadId);
      if (!fileItem) return;

      // Change to uploading state
      fileItem.status = 'uploading';
      this.activeUploads++;
      this.startProcessingTimer(uploadId);
      try {
        const formData = new FormData();
        formData.append('file', fileItem.file);
        
        // Add metadata from the file item (stored when file was added to queue)
        if (fileItem.company_id) {
          formData.append('company_id', fileItem.company_id);
        }
        if (fileItem.type) {
          formData.append('type', fileItem.type);
        }
        if (fileItem.is_hard_scan) {
          formData.append('is_hard_scan', '1');
        }
        if (fileItem.fromMultiPage) {
          // One invoice photographed page by page and stitched together — tell the
          // server not to run the multi-invoice-in-one-image split on it.
          formData.append('is_multi_page', '1');
        }

        fileItem.progress = 0;

        setInterval(() => {
            fileItem.progress += 1;
            if (fileItem.progress >= 98) {
                clearInterval();
            }
        }, 200);



        const response = await api.post('/api/invoices', formData, {
          headers: { 'Content-Type': 'multipart/form-data'  , 'x-requested-with': 'XMLHttpRequest' },
          onUploadProgress: (progressEvent) => {
            // Update upload progress during file transfer
            //const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          // const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            //fileItem.progress = percentCompleted;
          },
        });
        fileItem.progress = 100;
        // Upload complete, switch to processing
        fileItem.scanId = response.data.job_id;
        fileItem.invoiceId = response.data.job_id;
        fileItem.result = response.data;
        fileItem.status = 'processing';

        // Reset processing progress and start polling
        fileItem.processingProgress = 0;
        this.startProcessingTimer(uploadId);

        // Add to scans list for real-time tracking
        const newScan = {
          id: response.data.job_id,
          filename: fileItem.fileName,
          status: response.data.status || 'pending',
          progress: 0,
          result: null,
          error_message: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        this.scans.unshift(newScan);

      } catch (error) {
        fileItem.status = 'failed';
        fileItem.error = error.response?.data?.message || error.message;
      } finally {
        this.activeUploads--;
        // Process next file in queue
        this.processQueue();
      }
    },

    updateProgress(uploadId, progress) {
      const fileItem = this.uploadQueue.find(item => item.id === uploadId);
      if (fileItem) {
        fileItem.progress = progress;
      }
    },

    retryUpload(uploadId) {
      const fileItem = this.uploadQueue.find(item => item.id === uploadId);
      if (fileItem && fileItem.status === 'failed') {
        fileItem.status = 'queued';
        fileItem.error = null;
        fileItem.progress = 0;
        this.processQueue();
      }
    },

    cancelUpload(uploadId) {
      this.stopProcessingTimer(uploadId);
      const index = this.uploadQueue.findIndex(item => item.id === uploadId);
      if (index !== -1) {
        this.uploadQueue.splice(index, 1);
      }
    },

    removeFromQueue(uploadId) {
      this.cancelUpload(uploadId);
    },

    clearQueue() {
      // Clear all processing timers
      Object.keys(this.processingTimers).forEach(uploadId => {
        this.stopProcessingTimer(uploadId);
      });
      this.uploadQueue = [];
      this.activeUploads = 0;
    },

    // Start upload timer (tracks time from upload start)
    startUploadTimer(uploadId) {
      const fileItem = this.uploadQueue.find(item => item.id === uploadId);
      if (!fileItem) return;

      const timer = setInterval(() => {
        const elapsedSeconds = Math.floor((Date.now() - fileItem.startTime) / 1000);
        fileItem.processingTime = elapsedSeconds;
      }, 1000); // Update every second

      this.processingTimers[uploadId] = timer;
    },

    // Start processing timer for an upload
    startProcessingTimer(uploadId) {
      const fileItem = this.uploadQueue.find(item => item.id === uploadId);
      if (!fileItem) return;

      // Stop any existing timer for this upload
      this.stopProcessingTimer(uploadId);

      const maxTime = 60; // 60 seconds
      const startTime = Date.now();

      const timer = setInterval(async () => {
        // Re-find file item each time in case reference changed
        const currentFileItem = this.uploadQueue.find(item => item.id === uploadId);
        if (!currentFileItem) {
          this.stopProcessingTimer(uploadId);
          return;
        }

        const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);
        currentFileItem.processingTime = elapsedSeconds;
        currentFileItem.processingProgress = Math.min((elapsedSeconds / maxTime) * 100, 100);

        // Only check status if we have a scanId
        if (!currentFileItem.scanId) return;

        // Check if scan is completed by fetching latest data
        try {
          const response = await api.get(`/api/invoices/${currentFileItem.scanId}`);
          const status = response.data.status;
          
          if (status === 'completed' || status === 'failed') {
            // Update file item with final data
            currentFileItem.scanData = response.data.result;
            currentFileItem.status = status;
            currentFileItem.error = response.data.error_message || response.data.error;
            currentFileItem.processingProgress = 100;
            
            console.log(`✓ Processing ${status} for ${currentFileItem.fileName}`, {
              uploadId,
              scanId: currentFileItem.scanId,
              status,
              elapsed: elapsedSeconds
            });
            
            this.stopProcessingTimer(uploadId);
          }
        } catch (error) {
          console.error('Failed to check scan status:', error);
          
          // If we've been trying for a while and getting errors, give up
          if (elapsedSeconds >= maxTime) {
            currentFileItem.status = 'failed';
            currentFileItem.error = 'Processing timeout - could not verify completion';
            currentFileItem.processingProgress = 100;
            this.stopProcessingTimer(uploadId);
          }
        }
      }, 2000); // Check every 2 seconds

      this.processingTimers[uploadId] = timer;
    },

    // Stop processing timer
    stopProcessingTimer(uploadId) {
      if (this.processingTimers[uploadId]) {
        clearInterval(this.processingTimers[uploadId]);
        delete this.processingTimers[uploadId];
      }
    },

    // Fetch scans list
    async fetchScans(filters = {}) {
      this.loading = true;
      try {
        const response = await api.get('/api/invoices', { params: filters });
        this.scans = response.data.invoices || [];
        this.pagination = response.data.pagination || {
          total: response.data.invoices?.length || 0,
          per_page: filters.per_page || 12,
          current_page: filters.page || 1
        };
        return response.data;
      } catch (error) {
        console.error('Failed to fetch scans:', error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    // Fetch single scan
    async fetchScan(id) {
      try {
        const response = await api.get(`/api/invoices/${id}`);
        this.currentScan = response.data;
        return response.data;
      } catch (error) {
        console.error('Failed to fetch scan:', error);
        throw error;
      }
    },

    // Update scan status in list
    updateScanStatus(scanId, data) {
      const scan = this.scans.find(s => s.id === scanId);
      if (scan) {
        Object.assign(scan, data);
      }
    },

    // Delete scan
    async deleteScan(scanId) {
      try {
        await api.delete(`/api/invoices/${scanId}`);
        const index = this.scans.findIndex(s => s.id === scanId);
        if (index !== -1) {
          this.scans.splice(index, 1);
        }
        return { success: true };
      } catch (error) {
        return { success: false, error: error.response?.data?.message || error.message };
      }
    },

    // Start polling for status updates
    startPolling() {
      if (this.pollingInterval) return;

      this.pollingInterval = setInterval(async () => {
        // Get IDs from both scans list and upload queue
        const scanIds = this.processingScans.map(s => s.id);
        const queueIds = this.uploadQueue
          .filter(item => item.status === 'processing' && item.scanId)
          .map(item => item.scanId);

        const allIds = [...new Set([...scanIds, ...queueIds])];

        if (allIds.length === 0) return;

        try {
          const response = await api.get('/api/invoices/status', {
            params: { ids: allIds.join(',') }
          });

          response.data.statuses.forEach(status => {
            // Update scans list
            this.updateScanStatus(status.id, status);

            // Update upload queue items
            const queueItem = this.uploadQueue.find(item => item.scanId === status.id);
            if (queueItem) {
              if (status.status === 'completed' || status.status === 'failed') {
                queueItem.status = status.status;
                queueItem.processingProgress = 100;
                
                if (status.result) {
                  queueItem.scanData = status.result;
                }
                if (status.error || status.error_message) {
                  queueItem.error = status.error || status.error_message;
                }
                
                // Stop the timer for this item
                this.stopProcessingTimer(queueItem.id);
                
                console.log(`✓ Polling updated ${queueItem.fileName} to ${status.status}`);
              }
            }
          });
        } catch (error) {
          // Silently fail - endpoint might not exist yet or user not authenticated
          // Don't spam console on every poll
          if (error.response?.status !== 401 && error.response?.status !== 404) {
            console.error('Polling error:', error);
          }
        }
      }, 3000); // Poll every 3 seconds
    },

    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },
  },
});
